// SPDX-License-Identifier: Apache-2.0

import { approval, erc20Deposit, ethDeposit } from "../constants/Constants";

// What a swap needs in the user's own wallets before it can start.
//
// The pre-submit check used to test only that some balances were above zero
// and answered every shortfall with one generic "insufficient funds" message
// (mainnet, 2026-09-18: a user swapping ETH for CKB could not tell that the
// missing funds were CKB on Nervos). The real requirements differ per side:
//
// - Paying on Ethereum: the amount (which already includes the hub's fee) in
//   the paid asset, and the gas of the user's own deposit (and, for a token,
//   its approval) in ETH.
// - Any swap with CKB: the user opens the channel on Nervos, which locks the
//   channel cell and a padding as a deposit (ckbReserve), returned when the
//   swap completes; paying in CKB needs the amount on top.
// - Receiving on Ethereum needs nothing there: the hub concludes and
//   withdraws for the user.

export type SwapSideKind = "ETH" | "ERC20" | "CKB";

export type Chain = "Ethereum" | "Nervos";

// A balance as read from a wallet: base units, or undefined when it could not
// be read (a failed read must not look like an empty wallet).
export type ReadBalance = bigint | undefined;

export type Requirement = {
  chain: Chain;
  asset: string; // code shown to the user
  decimals: number;
  need: bigint; // base units
  have: ReadBalance;
};

// Gas price headroom for the user's own Ethereum transactions: the quote's gas
// price is the current one, the wallet may price the transaction higher.
const userGasHeadroomPercent = 25n;

export function userEthereumGas(kind: SwapSideKind, gasPrice: bigint): bigint {
  const units =
    kind === "ETH" ? BigInt(ethDeposit) : BigInt(erc20Deposit + approval);
  return (units * gasPrice * (100n + userGasHeadroomPercent)) / 100n;
}

export type SwapFundsInput = {
  from: { kind: SwapSideKind; code: string; decimals: number };
  to: { kind: SwapSideKind };
  fromAmount: bigint; // what the user pays, base units, hub fee included
  gasPrice: bigint; // wei, from the quote for the paying chain
  ckbReserve: bigint; // shannons
  balances: {
    from: ReadBalance; // the paid asset
    eth: ReadBalance; // ETH, when paying on Ethereum
    ckb: ReadBalance; // CKB, when the swap involves CKB
  };
};

// requirements lists every balance the swap needs, whether or not it is met.
export function requirements(s: SwapFundsInput): Requirement[] {
  const reqs: Requirement[] = [];
  const involvesCKB = s.from.kind === "CKB" || s.to.kind === "CKB";

  if (s.from.kind === "ETH") {
    reqs.push({
      chain: "Ethereum",
      asset: "ETH",
      decimals: 18,
      need: s.fromAmount + userEthereumGas("ETH", s.gasPrice),
      have: s.balances.from,
    });
  } else if (s.from.kind === "ERC20") {
    reqs.push({
      chain: "Ethereum",
      asset: s.from.code,
      decimals: s.from.decimals,
      need: s.fromAmount,
      have: s.balances.from,
    });
    reqs.push({
      chain: "Ethereum",
      asset: "ETH",
      decimals: 18,
      need: userEthereumGas("ERC20", s.gasPrice),
      have: s.balances.eth,
    });
  }

  if (involvesCKB) {
    reqs.push({
      chain: "Nervos",
      asset: "CKB",
      decimals: 8,
      need: (s.from.kind === "CKB" ? s.fromAmount : 0n) + s.ckbReserve,
      have: s.from.kind === "CKB" ? s.balances.from : s.balances.ckb,
    });
  }
  return reqs;
}

// shortfalls returns the requirements that are not met, and those whose
// balance could not be read.
export function shortfalls(reqs: Requirement[]): Requirement[] {
  return reqs.filter((r) => r.have === undefined || r.have < r.need);
}

// parseBalance turns a balance read by the form (a base-unit string, "" when
// the read failed) into a ReadBalance.
export function parseBalance(raw: string | undefined): ReadBalance {
  if (raw === undefined || raw.trim() === "") return undefined;
  try {
    return BigInt(raw.trim());
  } catch {
    return undefined;
  }
}
